import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Guscio } from './app/Guscio.tsx';
import { useSchermoLargo } from './app/useSchermoLargo.ts';
import { Home } from './home/Home.tsx';
import { ChiEAssente } from './assenti/ChiEAssente.tsx';
import { PaginaCalendario } from './calendario/PaginaCalendario.tsx';
import { Funzionari } from './funzionari/Funzionari.tsx';
import { AnalisiDati } from './analisi/AnalisiDati.tsx';
import { Anagrafica } from './anagrafica/Anagrafica.tsx';
import { DaDecidere } from './decisioni/DaDecidere.tsx';
import { Accesso } from './autenticazione/Accesso.tsx';
import { CambioPassword } from './autenticazione/CambioPassword.tsx';
import { PERCORSO_RECUPERO, RecuperoPassword } from './autenticazione/RecuperoPassword.tsx';
import {
  type DatiCalendario,
  type NuovaRichiesta,
  type Profilo,
  ErroreRisposta,
  api,
} from './api/client.ts';
import type { Dipendente, Evento, GiornoCalendario } from './api/tipi.ts';

/**
 * Radice dell'applicazione.
 *
 * Carica i dati dall'API una volta sola e li passa alle viste. Dopo un
 * inserimento ricarica: e' il modo piu' semplice per garantire che quello
 * che si vede sia quello che c'e' nel database, e con qualche migliaio di
 * eventi costa poco. Quando servira' si passera' a una cache per risorsa.
 */
export function App() {
  const schermoLargo = useSchermoLargo();
  const [profilo, setProfilo] = useState<Profilo | null>(null);
  const [avvio, setAvvio] = useState(true);

  // La sessione sta in un cookie httpOnly: l'unico modo di sapere se e'
  // ancora valida e' chiederlo al server.
  useEffect(() => {
    api
      .profilo()
      .then(setProfilo)
      .catch(() => setProfilo(null))
      .finally(() => setAvvio(false));
  }, []);

  // Il recupero della password si apre dal link dell'email, anche a chi ha
  // gia' una sessione: prima di tutto il resto, e senza aspettare il profilo.
  if (window.location.pathname === PERCORSO_RECUPERO) return <RecuperoPassword />;

  if (avvio) return <Attesa testo="Caricamento…" />;
  if (!profilo) return <Accesso onEntrato={setProfilo} />;

  // La password provvisoria l'ha vista chi l'ha consegnata, e magari sta su un
  // foglio: finche' non la cambiano, chi la legge puo' entrare come loro, e nel
  // registro risulterebbe la persona sbagliata ad aver inserito una richiesta.
  if (profilo.deveCambiarePassword) {
    return (
      <CambioPassword
        nomeUtente={profilo.nomeCompleto}
        onCambiata={() => setProfilo({ ...profilo, deveCambiarePassword: false })}
      />
    );
  }

  return (
    <Applicazione
      profilo={profilo}
      schermoLargo={schermoLargo}
      onUscita={() => setProfilo(null)}
    />
  );
}

function Applicazione({
  profilo,
  schermoLargo,
  onUscita,
}: {
  profilo: Profilo;
  schermoLargo: boolean;
  onUscita: () => void;
}) {
  // L'anno mostrato. Parte da quello corrente - sempre il primo dell'elenco -
  // e da luglio si puo' passare al successivo per prenotare le ferie lunghe.
  const [anno, setAnno] = useState(profilo.anniDisponibili[0] ?? new Date().getFullYear());
  const [dati, setDati] = useState<DatiCalendario | null>(null);
  const [giorni, setGiorni] = useState<GiornoCalendario[]>([]);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = useCallback(async () => {
    try {
      const [dati, giorniDellAnno] = await Promise.all([api.calendario(anno), api.giorni(anno)]);
      setDati(dati);
      setGiorni(giorniDellAnno);
      setErrore(null);
    } catch (e) {
      setErrore(e instanceof ErroreRisposta ? e.message : 'Impossibile caricare i dati.');
    }
  }, [anno]);

  useEffect(() => {
    void carica();
  }, [carica]);

  /**
   * Salva una richiesta e ricarica.
   *
   * L'errore viene rilanciato perche' sia il pannello a mostrarlo: chi ha
   * appena premuto Invia deve leggere il motivo li' dove stava lavorando, non
   * in una barra in cima alla pagina.
   */
  const inviaRichiesta = useCallback(
    async (richiesta: NuovaRichiesta) => {
      await api.creaEvento(richiesta);
      await carica();
    },
    [carica],
  );

  const annulla = useCallback(
    async (evento: Evento) => {
      await api.annullaEvento(evento.id, evento.versione);
      await carica();
    },
    [carica],
  );

  const modificabili = useMemo(
    () => new Set(dati?.dipendentiModificabili ?? []),
    [dati?.dipendentiModificabili],
  );

  const io = useMemo<Dipendente | null>(
    () => dati?.dipendenti.find((d) => d.id === profilo.dipendenteId) ?? null,
    [dati, profilo.dipendenteId],
  );

  const mieiEventi = useMemo(
    () => (dati?.eventi ?? []).filter((e) => e.dipendenteId === profilo.dipendenteId),
    [dati, profilo.dipendenteId],
  );

  if (errore) return <Attesa testo={errore} />;
  if (!dati) return <Attesa testo="Caricamento dei dati…" />;

  /*
   * Un account di servizio non ha una propria home.
   *
   * Non compare fra i dipendenti - e' escluso dal calendario apposta - quindi
   * `io` resta nullo. Prima qui si aspettava anche `io`, e per quell'utenza
   * l'applicazione restava per sempre su "Caricamento dei dati…": un account
   * creato per amministrare non riusciva ad entrare.
   *
   * Chi amministra e basta parte dall'anagrafica, che e' il motivo per cui
   * quell'accesso esiste.
   */
  const diServizio = io === null;

  return (
    <BrowserRouter>
      <Routes>
        <Route
          element={
            <Guscio
              nomeUtente={profilo.nomeCompleto}
              anni={profilo.anniDisponibili}
              anno={anno}
              onAnno={setAnno}
              permessi={{
                home: !diServizio,
                anagrafica: profilo.puoAccedereAnagrafica,
                decisioni: profilo.puoDecidereRichieste,
                reportistica: profilo.puoAccedereReportistica,
                funzionari: profilo.puoAccedereFunzionari,
              }}
              onEsci={async () => {
                await api.esci().catch(() => undefined);
                onUscita();
              }}
            />
          }
        >
          <Route
            index
            element={
              // Cambiando anno il calendarietto riparte da capo: il mese
              // mostrato e' uno stato iniziale, e senza un rimontaggio
              // resterebbe su un anno che non e' piu' quello caricato.
              // Il calendario invece se la cava da solo: si veda PaginaCalendario.
              io === null ? (
                /*
                 * Chi non e' un dipendente parte dall'anagrafica, ma solo se
                 * ci puo' entrare. Mandarlo li' senza il permesso lo faceva
                 * cadere sulla rotta di riserva, che rimanda a "/", che lo
                 * rimandava di nuovo all'anagrafica: React si ferma con
                 * "Maximum update depth exceeded" e l'applicazione muore.
                 * Oggi l'unico account di servizio e' amministratore e il
                 * caso non si presenta, ma niente nello schema impedisce di
                 * crearne un altro con un ruolo diverso, e chi lo creasse
                 * troverebbe una schermata bianca senza spiegazione.
                 */
                profilo.puoAccedereAnagrafica ? (
                  <Navigate to="/anagrafica" replace />
                ) : (
                  <Attesa testo="Questo accesso non ha una pagina personale: serve per amministrare, ma non ha i permessi per farlo. Contatta un amministratore." />
                )
              ) : (
              <Home
                key={anno}
                dipendente={io}
                eventi={mieiEventi}
                giorni={giorni}
                puoRiposoProduzione={profilo.puoInserireRiposoProduzione}
                ultimoGiornoInseribile={profilo.ultimoGiornoInseribile}
                primoGiornoInseribile={profilo.primoGiornoInseribile}
                annoMostrato={anno}
                verifica={api.verifica}
                onInvia={inviaRichiesta}
                onAnnulla={annulla}
              />
              )
            }
          />

          <Route
            path="/assenti"
            element={<ChiEAssente dipendenti={dati.dipendenti} eventi={dati.eventi} />}
          />

          {/* Il calendario esiste solo su schermi larghi: su un telefono una
              griglia da 365 colonne non e' utilizzabile. */}
          <Route
            path="/calendario"
            element={
              schermoLargo ? (
                <PaginaCalendario
                  anno={dati.anno}
                  giorni={giorni}
                  dipendenti={dati.dipendenti}
                  eventi={dati.eventi}
                  dipendentiModificabili={modificabili}
                  dipendenteProprio={io?.id ?? null}
                  puoRiposoProduzione={profilo.puoInserireRiposoProduzione}
                  ultimoGiornoInseribile={profilo.ultimoGiornoInseribile}
                  primoGiornoInseribile={profilo.primoGiornoInseribile}
                  verifica={api.verifica}
                  onInvia={inviaRichiesta}
                  onAnnulla={annulla}
                />
              ) : (
                <Attesa testo="Il calendario è disponibile solo su schermi più larghi." />
              )
            }
          />

          {profilo.puoAccedereFunzionari && (
            <Route
              path="/funzionari"
              element={
                <Funzionari
                  dipendenti={dati.dipendenti}
                  eventi={dati.eventi}
                  programmi={dati.programmi}
                />
              }
            />
          )}

          {profilo.puoAccedereReportistica && (
            <Route
              path="/analisi"
              element={
                <AnalisiDati
                  anno={dati.anno}
                  dipendenti={dati.dipendenti}
                  eventi={dati.eventi}
                  giorni={giorni}
                />
              }
            />
          )}

          {/* Come per l'anagrafica: la rotta esiste solo per chi ha il
              permesso, e chi non ce l'ha finisce sulla home invece che su
              una pagina che l'API gli rifiuterebbe. */}
          {profilo.puoDecidereRichieste && (
            <Route
              path="/da-decidere"
              element={
                <DaDecidere
                  dipendenti={dati.dipendenti}
                  eventi={dati.eventi}
                  dipendenteProprio={profilo.dipendenteId}
                  onDecisa={carica}
                  onAggiorna={carica}
                />
              }
            />
          )}

          {/* La rotta esiste solo per chi amministra: chi non lo e' viene
              rimandato alla home dalla rotta di riserva qui sotto, invece di
              vedere una pagina che l'API gli rifiuterebbe comunque. */}
          {profilo.puoAccedereAnagrafica && (
            <Route
              path="/anagrafica"
              element={<Anagrafica dipendenteProprio={profilo.dipendenteId} />}
            />
          )}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function Attesa({ testo }: { testo: string }) {
  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--testo-tenue)' }}>
      {testo}
    </div>
  );
}
